import * as categoriesActionTypes from './categoriesActionTypes';
import piction from '../utils/piction';
import appConfig from '../appConfig';
import { addError, addIntlInfo } from './messageDialog';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired } from './session';

export const cancelAllRequests = () => ({
  type: categoriesActionTypes.CATEGORIES_CANCEL_ALL_REQUESTS,
});

export const retrieveCategoriesRequest = (categoryID, requestPromise) => ({
  type: categoriesActionTypes.CATEGORIES_RETRIEVE_REQUEST,
  categoryID,
  requestPromise,
});

export const retrieveCategoriesSuccess = (categoryID, categoryList) => ({
  type: categoriesActionTypes.CATEGORIES_RETRIEVE_SUCCESS,
  categoryID,
  categoryList,
});

export const retrieveCategoriesFailure = (categoryID, err) => ({
  type: categoriesActionTypes.CATEGORIES_RETRIEVE_FAILURE,
  categoryID,
  err,
});

export const retrieveCategoryIndicator = (categoryID) => {
  return function retrieveCategoryIndicatorDispatch(dispatch, getState) {
    piction.getCategories(null, { 
      CATEGORY_ID: categoryID, 
      RETURN_COLOUR: 'TRUE',
      SINGLE_CATEGORY_ONLY: 'TRUE'
    }).then((res) => {
      if (!res.res?.ct?.length) {
        return console.error('category not found');
      };
      dispatch({
        type: categoriesActionTypes.CATEGORIES_NUMBER_INDICATOR_UPDATE,
        categoryID,
        indicator: res.res.ct[0].x
      });
    });
  };
};

export const retrieveCategories = categoryID =>
  function retrieveCategoriesDispatch(dispatch) {
    const options = { CATEGORY_ID: categoryID, RETURN_COLOUR: 'TRUE' };

    if (appConfig.restrictEditing) {
      options.READ_ONLY_CHECK = 'TRUE';
    }

    if (appConfig.categoriesSortByCC) {
      options.SORT_ON = 'IGNORE';
    }

    if (!categoryID) {
      options.RETURN_COLLECTION_DETAILS = 'TRUE';
    }

    const promise = piction.getCategories(null, options)
      .then((res) => {
        dispatch(retrieveCategoriesSuccess(categoryID, res.res));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveCategoriesFailure(categoryID, err));
        }
        return err;
      });
    dispatch(retrieveCategoriesRequest(categoryID, promise));
    return promise;
  };

export const clearPanelTreeList = categoryID => ({
  type: categoriesActionTypes.CATEGORIES_LIST_CLEAR,
  categoryID,
});

export const toggleCollectionCollapse = categoryID => ({
  type: categoriesActionTypes.COLLECTION_COLLAPSE_TOGGLE,
  categoryID,
});

export const createCategoryRequest = (
  categoryName,
  categoryParentID,
  categoryDescription,
  requestPromise,
) => ({
  type: categoriesActionTypes.CATEGORIES_CREATE_REQUEST,
  categoryName,
  categoryParentID,
  categoryDescription,
  requestPromise,
});

export const createCategorySuccess = (
  categoryID,
  categoryName,
  categoryParentID,
  categoryDescription,
) => ({
  type: categoriesActionTypes.CATEGORIES_CREATE_SUCCESS,
  categoryID,
  categoryName,
  categoryParentID,
  categoryDescription,
});

export const createCategoryFailure = (categoryName, categoryParentID, err) => ({
  type: categoriesActionTypes.CATEGORIES_CREATE_FAILURE,
  categoryName,
  categoryParentID,
  err,
});

export const createRootCategory = (
  categoryName,
  categoryParentID = '0',
  collectionID,
  categoryDescription = ''
) =>
  function createCategorysDispatch(dispatch, getState) {
    const store = getState().categories;

    const foundRequestIndex = store
      .get('createRequests')
      .findKey(
        (createRequest) =>
          createRequest.get('categoryName') === categoryName &&
          createRequest.get('categoryParentID') === collectionID
      );

    if (
      foundRequestIndex !== undefined &&
      !(
        store.getIn(['createRequests', foundRequestIndex, 'request']) instanceof
        Error
      )
    ) {
      return Promise.resolve(
        new Error('Request with same details already being made')
      );
    }

    const promise = piction
      .createRootCategory(categoryName, collectionID, {
        'CATEGORY.ON_MOVE_INHERIT': 'T',
      })
      .then((res) => {
        dispatch(
          createCategorySuccess(
            res.id,
            categoryName,
            collectionID,
            categoryDescription
          )
        );
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(createCategoryFailure(categoryName, collectionID, err));
        }
        return err;
      });

    dispatch(
      createCategoryRequest(
        categoryName,
        collectionID,
        categoryDescription,
        promise
      )
    );
    return promise;
  };

export const createGlobalCategory = (
  categoryName,
  collectionID,
  categoryDescription = '',
) =>
  function createGlobalCategorysDispatch(dispatch, getState) {
    const store = getState().categories;

    const foundRequestIndex = store
      .get('createRequests')
      .findKey(
        (createRequest) =>
          createRequest.get('categoryName') === categoryName &&
          createRequest.get('categoryParentID') === collectionID
      );

    if (foundRequestIndex !== undefined &&
      !(store.getIn(['createRequests', foundRequestIndex, 'request']) instanceof Error)
    ) {
      return Promise.resolve(new Error('Request with same details already being made'));
    }

    const promise = piction.createRootCategory(categoryName, collectionID, {
      'CATEGORY.OWNED_BY': 'PHOTOGRAPHER',
      'CATEGORY.ON_MOVE_INHERIT': 'T',
      FORCE_REFRESH: 'TRUE',
      INHERIT_ROLES_FORCE: appConfig.enableGlobalCategoryCreation,
    })
      .then((res) => {
        dispatch(
          createCategorySuccess(
            res.id,
            categoryName,
            collectionID,
            categoryDescription,
          )
        );
        return res;
      })
      .then((res) => {
        dispatch(retrieveCategories(undefined, { forceRefresh: true }));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(createCategoryFailure(categoryName, collectionID, err));
        }
        return err;
      });

    dispatch(
      createCategoryRequest(
        categoryName,
        collectionID,
        categoryDescription,
        promise,
      )
    );
    return promise;
  };

export const createCategory = (
  categoryName,
  categoryParentID = '0',
  collectionID,
  categoryDescription = ''
) =>
  function createCategorysDispatch(dispatch, getState) {
    const store = getState().categories;

    const foundRequestIndex = store
      .get('createRequests')
      .findKey(
        (createRequest) =>
          createRequest.get('categoryName') === categoryName &&
          createRequest.get('categoryParentID') === categoryParentID
      );

    if (
      foundRequestIndex !== undefined &&
      !(
        store.getIn(['createRequests', foundRequestIndex, 'request']) instanceof
        Error
      )
    ) {
      return Promise.resolve(
        new Error('Request with same details already being made')
      );
    }

    const promise = piction
      .createCategory(categoryName, {
        PARENT: categoryParentID,
        DESCRIPTION: categoryDescription,
        NO_ORDER_BY: 'T',
      })
      .then((res) => {
        dispatch(
          createCategorySuccess(
            res.id,
            categoryName,
            categoryParentID,
            categoryDescription,
          )
        );
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(createCategoryFailure(categoryName, categoryParentID, err));
        }
        return err;
      });

    dispatch(
      createCategoryRequest(
        categoryName,
        categoryParentID,
        categoryDescription,
        promise,
      )
    );
    return promise;
  };

export const editCategoryRequest = (
  categoryID,
  categoryName,
  categoryDescription,
  requestPromise
) => ({
  type: categoriesActionTypes.CATEGORIES_EDIT_REQUEST,
  categoryID,
  categoryName,
  categoryDescription,
  requestPromise,
});

export const editCategorySuccess = (
  categoryID,
  categoryName,
  categoryDescription,
) => ({
  type: categoriesActionTypes.CATEGORIES_EDIT_SUCCESS,
  categoryID,
  categoryName,
  categoryDescription,
});

export const editCategoryFailure = (categoryID, err) => ({
  type: categoriesActionTypes.CATEGORIES_EDIT_FAILURE,
  categoryID,
  err,
});

export const editCategory = (categoryID, categoryName, categoryDescription) =>
  function editCategoryDispatch(dispatch) {
    const options = {
      TITLE: categoryName,
      DESCRIPTION: categoryDescription,
      NO_ORDER_BY: 'T',
    };
    const promise = piction
      .editCategory(categoryID, options)
      .then((res) => {
        dispatch(
          editCategorySuccess(categoryID, categoryName, categoryDescription)
        );
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(editCategoryFailure(categoryID, err));
        }
        return err;
      });

    dispatch(
      editCategoryRequest(
        categoryID,
        categoryName,
        categoryDescription,
        promise,
      )
    );
    return promise;
  };

export const deleteCategoryRequest = (categoryID, requestPromise) => ({
  type: categoriesActionTypes.CATEGORIES_DELETE_REQUEST,
  categoryID,
  requestPromise,
});

export const deleteCategorySuccess = categoryID => ({
  type: categoriesActionTypes.CATEGORIES_DELETE_SUCCESS,
  categoryID,
});

export const deleteCategoryFailure = (categoryID, err) => ({
  type: categoriesActionTypes.CATEGORIES_DELETE_FAILURE,
  categoryID,
  err,
});

export const deleteCategory = (categoryID) =>
  function deleteCategoryDispatch(dispatch) {
    const promise = piction
      .deleteCategory(categoryID)
      .then((res) => {
        dispatch(deleteCategorySuccess(categoryID, res.res.ct));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(deleteCategoryFailure(categoryID, err));
          dispatch(addError(err.message));
        }
        return err;
      });

    dispatch(deleteCategoryRequest(categoryID, promise));
    return promise;
  };

export const purgeRecyclebinRequest = (categoryID, requestPromise) => ({
  type: categoriesActionTypes.CATEGORIES_PURGE_RECYCLEBIN_REQUEST,
  categoryID,
  requestPromise,
});

export const purgeRecyclebinSuccess = categoryID => ({
  type: categoriesActionTypes.CATEGORIES_PURGE_RECYCLEBIN_SUCCESS,
  categoryID,
});

export const purgeRecyclebinFailure = (categoryID, err) => ({
  type: categoriesActionTypes.CATEGORIES_PURGE_RECYCLEBIN_FAILURE,
  categoryID,
  err,
});

export const purgeRecyclebin = (categoryID) =>
  function purgeRecyclebinDispatch(dispatch) {
    const options = {
      ACTION: 'RECYCLE_BIN_PURGE',
      'CATEGORY.CATEGORY_ID': categoryID,
      ON_PURGE_DEL_SUBCATEGORIES: 'TRUE',
    };
    const promise = piction.request('MNG_CATEGORY_V2', options, { useJSON: true })
      .then((res) => {
        if (res.RESULT === 'SUCCESS') {
          dispatch(addIntlInfo('umoModifyDialog.inProgressMessage'));
          dispatch(clearPanelTreeList(categoryID, res.i));
        } else {
          console.log('purgeRecyclebin', res);
          dispatch(addError('searchResults.purgeFailed'));
          dispatch(purgeRecyclebinFailure(categoryID, res.i));
        }
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(purgeRecyclebinFailure(categoryID, err));
          dispatch(addError(err.message));
        }
        return err;
      });

    dispatch(purgeRecyclebinRequest(categoryID, promise));
    return promise;
  };
